import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import axios from 'axios'

import FloatingAdminMenu from './FloatMenu'
import AtomBackground    from './Atombackground'

import {
  FiLock, FiEye, FiEyeOff, FiLogOut,
  FiMail, FiRefreshCw, FiAlertCircle,
  FiTrash2, FiFilter, FiUsers,
} from 'react-icons/fi'

/* ─────────────────────────────────────
   CONFIG
───────────────────────────────────── */
const API      = 'https://www.medicinacondante.com/lista_espera.php'
const PASSWORD = 'dante.admin'

const CURSOS = [
  'Todos',
  'Neumología Intensiva',
  'Semiología Avanzada',
]

/* ─────────────────────────────────────
   ESTILOS GLOBALES
───────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

    :root {
      --gold:      #C9A84C;
      --gold-dim:  rgba(201,168,76,0.55);
      --gold-sub:  rgba(201,168,76,0.08);
      --gold-brd:  rgba(201,168,76,0.20);
      --bg:        #0A0906;
      --bg-2:      #0F0E0B;
      --bg-3:      #141208;
      --line:      rgba(201,168,76,0.12);
      --ink:       #EDE0C8;
      --ink-2:     rgba(237,224,200,0.62);
      --ink-3:     rgba(237,224,200,0.32);
      --green:     #6DBF8F;
      --red:       #E07A7A;
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--bg);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    ::placeholder {
      color: var(--ink-3);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 300;
      font-style: italic;
    }

    @keyframes adm-spin { to { transform: rotate(360deg); } }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }
  `}</style>
)

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.07 },
  }),
}

/* ─────────────────────────────────────
   HELPERS
───────────────────────────────────── */
const SectionLabel = ({ children, className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="inline-block w-5 h-px" style={{ background: 'var(--gold)', opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
      {children}
    </span>
  </div>
)

function formatDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
}

/* ─────────────────────────────────────
   WAITLIST CARD
───────────────────────────────────── */
const WaitlistCard = ({ item, index, onDelete }) => {
  const [confirming, setConfirming] = useState(false)

  const handleDelete = async () => {
    if (!confirming) { setConfirming(true); return }
    try {
      await axios.delete(`${API}?id=${item.id}`)
      onDelete(item.id)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="relative overflow-hidden"
      style={{ background: 'var(--bg-3)', border: '1px solid var(--line)' }}
    >
      {/* Accent top */}
      <div
        className="absolute top-0 left-6 right-6 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.28), transparent)' }}
      />

      <div style={{ padding: 'clamp(20px,3vw,28px)' }}>

        {/* Email + fecha */}
        <div
          className="flex items-start gap-3 mb-4 pb-4"
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          <div
            className="flex-shrink-0 flex items-center justify-center"
            style={{ width: 40, height: 40, background: 'var(--gold-sub)', border: '1px solid var(--gold-brd)', color: 'var(--gold)' }}
          >
            <FiMail size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.01em', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.email}
            </p>
            <p style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 3 }}>
              {formatDate(item.created_at)}
            </p>
          </div>
        </div>

        {/* Curso */}
        <div className="flex items-center gap-2 mb-5">
          <div
            className="inline-flex items-center gap-2 px-3 py-1"
            style={{ background: 'var(--gold-sub)', border: '1px solid var(--gold-brd)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--gold)', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)' }}>
              {item.curso || 'Todos los cursos'}
            </span>
          </div>
        </div>

        {/* Botón eliminar */}
        <motion.button
          whileHover={{ opacity: 0.85, x: 2 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleDelete}
          onBlur={() => setConfirming(false)}
          className="w-full flex items-center justify-center gap-2.5"
          style={{
            padding: '10px 0',
            background: confirming ? 'rgba(224,122,122,0.15)' : 'transparent',
            border: `1px solid ${confirming ? 'rgba(224,122,122,0.4)' : 'rgba(224,122,122,0.15)'}`,
            color: 'var(--red)',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            transition: 'all 0.2s',
          }}
        >
          <FiTrash2 size={13} />
          {confirming ? 'Confirmar eliminación' : 'Eliminar'}
        </motion.button>
      </div>
    </motion.div>
  )
}

/* ─────────────────────────────────────
   TOTALES POR CURSO
───────────────────────────────────── */
const TotalesBadges = ({ totales }) => {
  if (!totales?.length) return null
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {totales.map(t => (
        <div
          key={t.curso}
          className="flex items-center gap-2 px-3 py-1"
          style={{ background: 'var(--gold-sub)', border: '1px solid var(--gold-brd)' }}
        >
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            {t.curso || 'General'}
          </span>
          <span style={{ fontSize: 10, fontWeight: 300, color: 'var(--ink-3)' }}>
            {t.total}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminListaEspera = () => {
  const [data,          setData]          = useState([])
  const [totales,       setTotales]       = useState([])
  const [filtro,        setFiltro]        = useState('Todos')
  const [isAuth,        setIsAuth]        = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [showPassword,  setShowPassword]  = useState(false)
  const [errorAuth,     setErrorAuth]     = useState('')
  const [loading,       setLoading]       = useState(false)
  const [showMenu,      setShowMenu]      = useState(false)

  /* ── Auth ── */
  useEffect(() => {
    if (localStorage.getItem('admin_auth') === 'true') setIsAuth(true)
  }, [])

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true)
      localStorage.setItem('admin_auth', 'true')
      setErrorAuth('')
    } else {
      setErrorAuth('Contraseña incorrecta')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_auth')
    setIsAuth(false)
  }

  /* ── Fetch ── */
  const fetchData = async () => {
    try {
      setLoading(true)
      const url = filtro !== 'Todos'
        ? `${API}?curso=${encodeURIComponent(filtro)}`
        : API
      const res = await axios.get(url)
      setData(res.data.data    || [])
      setTotales(res.data.totales || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (isAuth) fetchData() }, [isAuth, filtro])

  const handleDelete = (id) => setData(prev => prev.filter(i => i.id !== id))

  /* ── Datos filtrados localmente (ya filtra el backend, pero por si acaso) ── */
  const displayed = filtro === 'Todos'
    ? data
    : data.filter(i => i.curso === filtro || (!i.curso && filtro === 'Todos'))

  /* ════════════════════════════
     RENDER
  ════════════════════════════ */
  return (
    <>
      <GlobalStyles />

      <div
        className="relative min-h-screen overflow-x-hidden"
        style={{ background: 'var(--bg)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.07) 0%, transparent 55%)' }}
        />

        {/* Top line */}
        <div
          className="fixed top-0 left-0 right-0 h-px z-50"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)' }}
        />

        {/* ─────── LOGIN ─────── */}
        <AnimatePresence>
          {!isAuth && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            >
              <div
                className="absolute inset-0"
                style={{ background: 'rgba(10,9,6,0.96)', backdropFilter: 'blur(14px)' }}
              />

              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <AtomBackground />
              </div>

              <motion.div
                initial={{ scale: 0.88, y: 28, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.88, y: 28, opacity: 0 }}
                transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-sm shadow-2xl"
                style={{ background: 'var(--bg-3)', border: '1px solid var(--line)' }}
              >
                <div
                  className="absolute top-0 left-8 right-8 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)' }}
                />

                <div className="p-8 sm:p-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                      style={{ border: '1px solid var(--line)', background: 'var(--gold-sub)', color: 'var(--gold)' }}
                    >
                      <FiLock size={16} />
                    </div>
                    <div>
                      <SectionLabel>Acceso restringido</SectionLabel>
                      <h2
                        className="mt-1"
                        style={{ fontSize: 'clamp(1rem,3vw,1.2rem)', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.01em' }}
                      >
                        Lista de Espera Admin
                      </h2>
                    </div>
                  </div>

                  <div className="h-px mb-6" style={{ background: 'var(--line)' }} />

                  <div className="relative mb-3">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordInput}
                      onChange={e => setPasswordInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleLogin()}
                      placeholder="Contraseña de acceso"
                      style={{
                        background: 'var(--bg-2)', color: 'var(--ink)',
                        border: '1px solid var(--line)', outline: 'none',
                        width: '100%', height: 48, padding: '0 48px 0 16px',
                        fontSize: 14, fontWeight: 300, borderRadius: 0,
                      }}
                    />
                    <button
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                      style={{ background: 'none', border: 'none', color: 'var(--gold-dim)', cursor: 'pointer' }}
                    >
                      {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>

                  <AnimatePresence>
                    {errorAuth && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="flex items-center gap-2 mb-3"
                        style={{ fontSize: 12, color: 'var(--red)' }}
                      >
                        <FiAlertCircle size={11} /> {errorAuth}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ opacity: 0.86 }} whileTap={{ scale: 0.97 }}
                    onClick={handleLogin}
                    className="w-full"
                    style={{
                      padding: '14px 0', background: 'var(--gold)', color: '#0A0906',
                      fontSize: 12, fontWeight: 700, letterSpacing: '0.12em',
                      textTransform: 'uppercase', border: 'none', cursor: 'pointer',
                    }}
                  >
                    Ingresar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Menú flotante */}
        {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

        {/* ─────── PANEL ─────── */}
        {isAuth && (
          <div
            className="relative z-10 w-full max-w-6xl mx-auto"
            style={{ padding: 'clamp(80px,10vw,120px) clamp(16px,5vw,48px) 96px' }}
          >
            {/* HEADER */}
            <motion.div
              variants={fadeUp} initial="hidden" animate="show"
              className="mb-10"
            >
              <SectionLabel className="mb-3">Panel de administración</SectionLabel>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <h1 style={{ fontSize: 'clamp(1.75rem,4vw,2.8rem)', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.02em', lineHeight: 1.04 }}>
                    Lista de{' '}
                    <em style={{ color: 'var(--gold)', fontStyle: 'normal' }}>Espera</em>
                  </h1>
                  <p className="mt-2" style={{ fontSize: 13, fontWeight: 300, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                    Personas anotadas para próximos cursos IDM
                  </p>
                  <TotalesBadges totales={totales} />
                </div>

                <div className="flex items-center gap-3">
                  {data.length > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <SectionLabel className="mb-1">Registros</SectionLabel>
                      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>
                        {displayed.length}
                      </p>
                    </div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={fetchData}
                    className="w-10 h-10 flex items-center justify-center"
                    style={{ border: '1px solid var(--line)', color: 'var(--gold)', background: 'transparent', cursor: 'pointer' }}
                  >
                    <FiRefreshCw size={14} className={loading ? 'adm-spin' : ''} />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={handleLogout}
                    className="w-10 h-10 flex items-center justify-center"
                    style={{ border: '1px solid rgba(224,122,122,0.2)', color: 'var(--red)', background: 'transparent', cursor: 'pointer' }}
                  >
                    <FiLogOut size={14} />
                  </motion.button>
                </div>
              </div>

              {/* Separador */}
              <motion.div
                className="h-px mt-6"
                style={{ background: 'linear-gradient(90deg, var(--gold) 0%, transparent 100%)', opacity: 0.14 }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              />
            </motion.div>

            {/* FILTRO POR CURSO */}
            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="flex items-center gap-2 flex-wrap mb-8"
            >
              <div className="flex items-center gap-2 mr-2" style={{ color: 'var(--gold-dim)' }}>
                <FiFilter size={12} />
                <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                  Filtrar
                </span>
              </div>
              {CURSOS.map(c => (
                <button
                  key={c}
                  onClick={() => setFiltro(c)}
                  style={{
                    padding: '6px 14px',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: filtro === c ? 'var(--gold)' : 'var(--line)',
                    background:  filtro === c ? 'var(--gold-sub)' : 'transparent',
                    color:       filtro === c ? 'var(--gold)' : 'var(--ink-3)',
                    transition: 'all 0.18s',
                  }}
                >
                  {c}
                </button>
              ))}
            </motion.div>

            {/* LOADING */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-32 gap-5"
                >
                  <div
                    className="adm-spin"
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--line)', borderTopColor: 'var(--gold)' }}
                  />
                  <p style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--gold-dim)' }}>
                    Cargando lista
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* EMPTY */}
            {!loading && displayed.length === 0 && (
              <motion.div
                variants={fadeUp} initial="hidden" animate="show"
                className="flex flex-col items-center justify-center py-32 gap-4"
              >
                <div
                  className="w-14 h-14 flex items-center justify-center"
                  style={{ border: '1px solid var(--line)', color: 'var(--gold-dim)' }}
                >
                  <FiUsers size={20} />
                </div>
                <p style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                  No hay registros por el momento
                </p>
                <div className="h-px w-20" style={{ background: 'var(--line)' }} />
              </motion.div>
            )}

            {/* GRID DE CARDS */}
            {!loading && displayed.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayed.map((item, i) => (
                  <WaitlistCard
                    key={item.id}
                    item={item}
                    index={i}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default AdminListaEspera